/**
 * render.ts — canvas visualization.
 *
 * Ported from the prototype's `draw()`: the note ring, the swing plate + clapper
 * arm + trail, the wind compass (arrow shows where wind comes FROM), strike
 * flashes/ripples, and the temperature -> accent color.
 *
 * The renderer reads `engine.getViewState()` each frame and the current weather
 * (for tempF + windDirFrom, which are inputs, not part of ViewState). It keeps
 * NO copy of simulation state. The plate trail and the per-node ripple clocks are
 * purely visual artifacts derived from observed frames, so they live here.
 */
import { midiName } from '@windchime/engine';
import type { ViewState } from '@windchime/shared';

/** Weather inputs the renderer needs that aren't in ViewState. */
export interface RenderWeather {
  tempF: number;
  /** degrees, where the wind comes FROM */
  windDirFrom: number;
}

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/** Accent follows the MODE (bright Lydian = warm amber → dark Locrian = cold cyan),
 *  so the palette shifts whether the mode came from temperature-auto or was picked
 *  by hand — previously it tracked raw temperature and ignored manual choices. */
function accentForMode(modeIdx: number): string {
  const t = Math.max(0, Math.min(1, modeIdx / 6));
  const h = lerp(38, 190, t),
    s = lerp(78, 55, t),
    l = lerp(60, 62, t);
  return `hsl(${h} ${s}% ${l}%)`;
}

function angDiff(a: number, b: number): number {
  const d = Math.abs(a - b) % (2 * Math.PI);
  return d > Math.PI ? 2 * Math.PI - d : d;
}

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));

export class Renderer {
  private g: CanvasRenderingContext2D;
  private W2 = 0;
  private H2 = 0;
  private DPR = 1;
  /** toggle: draw note-name labels around the ring */
  showRing = false;

  // render-only state (visual only; not a copy of engine sim state)
  private trail: Array<{ x: number; y: number }> = [];
  private ripple = new Map<number, number>(); // midi -> seconds since last strike
  private lastFlash = new Map<number, number>(); // midi -> flash last frame (to detect strikes)
  // side-view strike animation (poisson mode): the clapper visually reaches the
  // struck tube at note time, then eases home. Purely visual, derived from flashes.
  private sideHit: { x: number; y: number; t: number } | null = null;
  private sideFlashPrev = new Map<number, number>();

  constructor(private cv: HTMLCanvasElement) {
    const ctx = cv.getContext('2d');
    if (!ctx) throw new Error('2D canvas context unavailable');
    this.g = ctx;
    this.resize();
  }

  resize(): void {
    this.DPR = Math.min(2, window.devicePixelRatio || 1);
    const r = this.cv.getBoundingClientRect();
    this.W2 = r.width;
    this.H2 = r.height;
    this.cv.width = this.W2 * this.DPR;
    this.cv.height = this.H2 * this.DPR;
    this.g.setTransform(this.DPR, 0, 0, this.DPR, 0, 0);
  }

  draw(vs: ViewState, w: RenderWeather, dt: number): void {
    const g = this.g,
      W2 = this.W2,
      H2 = this.H2;
    // split the canvas: top-down view on the left, a side elevation on the right.
    // The side column narrows on phones instead of disappearing; the compass pulls
    // in slightly when space is tight so the N/E/S/W labels stay in frame.
    const narrow = W2 < 520;
    // the side view takes the right 36%; the overhead circle sits at 40% of its
    // region (mirrored by the 51% first track in the toggle/key CSS grids, whose
    // centre = 25.5% of the band = 0.40 × the 64% overhead region)
    const sideW = clamp(W2 * (narrow ? 0.34 : 0.36), narrow ? 140 : 150, 300);
    const overW = W2 - sideW;
    const compact = overW < 520 || H2 < 420;
    const cx = overW * (narrow ? 0.5 : 0.4),
      cy = H2 * (compact ? 0.5 : 0.46),
      R = Math.min(overW, H2) * (narrow ? 0.23 : compact ? 0.3 : 0.32);
    const accent = accentForMode(vs.mode.index);
    document.documentElement.style.setProperty('--accent', accent);

    g.clearRect(0, 0, W2, H2);

    // faint guide ring
    g.lineWidth = 1;
    g.strokeStyle = 'rgba(120,140,160,.08)';
    g.beginPath();
    g.arc(cx, cy, R, 0, 2 * Math.PI);
    g.stroke();

    // active-arc wedge
    const az = vs.swingAzimuth,
      arc = vs.arcHalfWidth,
      mag = vs.swingMagnitude;
    if (mag > 0.04) {
      const grad = g.createRadialGradient(cx, cy, R * 0.2, cx, cy, R * 1.15);
      grad.addColorStop(0, 'rgba(127,212,212,0)');
      grad.addColorStop(1, accent.replace('hsl', 'hsla').replace(')', ` / ${0.1 + 0.14 * mag})`));
      g.fillStyle = grad;
      g.beginPath();
      g.moveTo(cx, cy);
      g.arc(cx, cy, R * 1.15, az - arc, az + arc);
      g.closePath();
      g.fill();
    }

    // ring nodes (in contact mode each tube sits at its live radius — recoil/sway)
    const contact = vs.strikeModel === 'contact';
    // the clapper's live polar position — used to light the catch zone it's actually in
    const clapAng = Math.atan2(vs.clapper.y, vs.clapper.x);
    const clapR = Math.hypot(vs.clapper.x, vs.clapper.y);
    // the reach ring: how far the puck's leading surface can extend (clapperReach + puck radius).
    // A strike needs the puck to BOTH reach a tube and be inside its (puck-widened) face arc —
    // drawing both makes a near-miss (puck short of the ring, or off a face) visually obvious.
    const puckR = R * vs.clapperRadius;
    if (contact) {
      g.save();
      g.setLineDash([3, 6]);
      g.strokeStyle = accent.replace('hsl', 'hsla').replace(')', ' / 0.16)');
      g.lineWidth = 1;
      g.beginPath();
      g.arc(cx, cy, R * vs.clapperReach + puckR, 0, 2 * Math.PI);
      g.stroke();
      g.restore();
    }
    for (const nd of vs.ring) {
      const ringR = contact ? R * (nd.radius ?? 1) : R;
      const nx = cx + ringR * Math.cos(nd.angle),
        ny = cy + ringR * Math.sin(nd.angle);
      const live = angDiff(nd.angle, az) < arc && mag > 0.04;
      // catch zone: the ±barHalfWidth face the clapper must enter to strike this tube.
      // Brightens as the clapper sweeps into the zone, brighter still once it has reached
      // the tube's radius (i.e. the geometric strike condition is met this frame).
      if (contact) {
        // the puck widens both the angular catch zone and the radial reach
        const faceSpan = vs.barHalfWidth + Math.asin(clamp(vs.clapperRadius / Math.max(nd.radius ?? 1, 1e-6), 0, 1));
        const inFace = angDiff(nd.angle, clapAng) < faceSpan;
        const reached = clapR + vs.clapperRadius >= ringR - 1e-3;
        g.beginPath();
        g.arc(cx, cy, ringR, nd.angle - faceSpan, nd.angle + faceSpan);
        g.strokeStyle = inFace
          ? accent.replace('hsl', 'hsla').replace(')', ` / ${reached ? 0.6 : 0.3})`)
          : 'rgba(120,140,160,.12)';
        g.lineWidth = inFace ? 2 : 1;
        g.stroke();
      }
      // advance / reset this node's ripple clock from observed flash
      const prev = this.lastFlash.get(nd.midi) ?? 0;
      const struck = nd.flash > prev + 1e-4; // flash only rises on a strike
      const rip = struck ? 0 : (this.ripple.get(nd.midi) ?? 0) + dt;
      this.ripple.set(nd.midi, rip);
      this.lastFlash.set(nd.midi, nd.flash);
      // ripple on strike
      if (nd.flash > 0.02) {
        const rr = rip * 120;
        g.strokeStyle = accent.replace('hsl', 'hsla').replace(')', ` / ${nd.flash * 0.5})`);
        g.lineWidth = 2;
        g.beginPath();
        g.arc(nx, ny, 8 + rr, 0, 2 * Math.PI);
        g.stroke();
      }
      const baseR = nd.isRoot ? 4.2 : 3;
      const r = baseR + nd.flash * 9;
      g.beginPath();
      g.arc(nx, ny, r, 0, 2 * Math.PI);
      if (nd.flash > 0.05) {
        g.fillStyle = accent;
        g.shadowColor = accent;
        g.shadowBlur = 22 * nd.flash;
      } else {
        g.fillStyle = live
          ? 'rgba(200,220,230,.85)'
          : nd.isRoot
            ? 'rgba(160,180,195,.7)'
            : 'rgba(120,140,155,.35)';
        g.shadowBlur = 0;
      }
      g.fill();
      g.shadowBlur = 0;
      if (this.showRing) {
        g.fillStyle = nd.flash > 0.1 ? accent : 'rgba(150,170,185,.45)';
        g.font = '10px "Spline Sans Mono", ui-monospace, monospace';
        g.textAlign = 'center';
        g.fillText(midiName(nd.midi), nx + Math.cos(nd.angle) * 12, ny + Math.sin(nd.angle) * 12 + 3);
      }
    }

    // plate trail (visual smoothing of observed plate positions)
    this.trail.push({ x: vs.plate.x, y: vs.plate.y });
    if (this.trail.length > 26) this.trail.shift();
    for (let i = 0; i < this.trail.length; i++) {
      const tp = this.trail[i],
        a = i / this.trail.length;
      g.beginPath();
      g.arc(cx + tp.x * R, cy + tp.y * R, 1.5 + a * 2, 0, 2 * Math.PI);
      g.fillStyle = accent.replace('hsl', 'hsla').replace(')', ` / ${a * 0.25})`);
      g.fill();
    }
    // clapper arm + plate
    const px = cx + vs.plate.x * R,
      py = cy + vs.plate.y * R;

    // wind sail (contact mode): the wind-catcher that drives the clapper. Its true swing
    // can be wide, so compress the drawn radius to keep it just outside the ring, in frame.
    if (contact) {
      const sMag = Math.hypot(vs.sail.x, vs.sail.y);
      if (sMag > 1e-3) {
        const k = ((Math.tanh(sMag * 0.6) * 1.4) / sMag) * R;
        const sx = cx + vs.sail.x * k,
          sy = cy + vs.sail.y * k;
        g.strokeStyle = 'rgba(150,170,185,.16)';
        g.lineWidth = 1;
        g.beginPath();
        g.moveTo(px, py);
        g.lineTo(sx, sy);
        g.stroke(); // sail cord
        // the sail is a rectangular plate that spins about the cord: seen from above it's a thin
        // bar whose orientation is sailAngle (it visibly rotates with the wind)
        g.save();
        g.translate(sx, sy);
        g.rotate(vs.sailAngle);
        g.fillStyle = accent.replace('hsl', 'hsla').replace(')', ' / 0.16)');
        g.strokeStyle = accent.replace('hsl', 'hsla').replace(')', ' / 0.45)');
        g.lineWidth = 1;
        g.beginPath();
        g.rect(-13, -3, 26, 6);
        g.fill();
        g.stroke();
        g.restore();
      }
    }

    g.strokeStyle = 'rgba(150,170,185,.25)';
    g.lineWidth = 1.2;
    g.beginPath();
    g.moveTo(cx, cy);
    g.lineTo(px, py);
    g.stroke();
    g.beginPath();
    g.arc(cx, cy, 3, 0, 2 * Math.PI);
    g.fillStyle = 'rgba(150,170,185,.4)';
    g.fill();
    // the clapper puck, drawn at its true radius (contact mode) — its size is the strike reach
    if (contact && puckR > 7) {
      g.beginPath();
      g.arc(px, py, puckR, 0, 2 * Math.PI);
      g.fillStyle = accent.replace('hsl', 'hsla').replace(')', ' / 0.14)');
      g.fill();
      g.lineWidth = 1.2;
      g.strokeStyle = accent.replace('hsl', 'hsla').replace(')', ' / 0.5)');
      g.stroke();
    }
    g.beginPath();
    g.arc(px, py, 7, 0, 2 * Math.PI);
    g.fillStyle = accent;
    g.shadowColor = accent;
    g.shadowBlur = 24;
    g.fill();
    g.shadowBlur = 0;

    // compass: where the wind comes FROM
    const cR = R * 1.34,
      wf = (w.windDirFrom * Math.PI) / 180;
    const wx = cx + Math.sin(wf) * cR,
      wy = cy - Math.cos(wf) * cR;
    g.fillStyle = 'rgba(140,160,175,.5)';
    g.font = '11px "Spline Sans Mono", ui-monospace, monospace';
    g.textAlign = 'center';
    ['N', 'E', 'S', 'W'].forEach((d, i) => {
      const a = (i * Math.PI) / 2;
      g.fillText(d, cx + Math.sin(a) * (cR + 14), cy - Math.cos(a) * (cR + 14) + 4);
    });
    g.strokeStyle = 'rgba(200,220,230,.5)';
    g.lineWidth = 1.5;
    g.beginPath();
    g.moveTo(wx, wy);
    g.lineTo(cx + (wx - cx) * 0.78, cy + (wy - cy) * 0.78);
    g.stroke();
    g.beginPath();
    g.arc(wx, wy, 3.5, 0, 2 * Math.PI);
    g.fillStyle = 'rgba(220,235,245,.7)';
    g.fill();

    // a 2-D side elevation of the chime, to the right of the top-down view
    this.drawSideView(overW, sideW, H2, vs, accent, dt);
  }

  /**
   * A side elevation of the chime spanning the full height: ring hook → suspension
   * cord → platform → the hanging tubes (length ~ pitch, lit on strike) → the clapper
   * → the wind sail. Everything sways with the same engine state (the clapper's
   * horizontal swing; the sail wider, and synthesised in poisson mode where it isn't
   * simulated).
   */
  private drawSideView(x0: number, sw: number, h: number, vs: ViewState, accent: string, dt: number): void {
    const g = this.g;
    const cx = x0 + sw / 2;
    const top = h * 0.04,
      bot = h * 0.96; // the chime spans (almost) the full height of the overhead view
    const hookY = top;
    const platformY = top + (bot - top) * 0.09;
    // on a narrow column the wide-swinging sail would bleed across the divider
    const slim = Math.min(1, sw / 150);
    const a = (alpha: number): string => accent.replace('hsl', 'hsla').replace(')', ` / ${alpha})`);

    g.save();
    g.beginPath();
    g.rect(x0, 0, sw, h);
    g.clip();

    // ring hook (fixed top anchor) + suspension cord down to the platform
    g.strokeStyle = 'rgba(170,190,205,.55)';
    g.lineWidth = 2;
    g.beginPath();
    g.arc(cx, hookY + 7, 5, Math.PI * 0.1, Math.PI * 1.5);
    g.stroke();
    g.strokeStyle = 'rgba(150,170,185,.3)';
    g.lineWidth = 1;
    g.beginPath();
    g.moveTo(cx, hookY + 12);
    g.lineTo(cx, platformY);
    g.stroke();
    // suspension platform
    const platW = sw * 0.62;
    g.strokeStyle = 'rgba(170,190,205,.6)';
    g.lineWidth = 3;
    g.beginPath();
    g.moveTo(cx - platW / 2, platformY);
    g.lineTo(cx + platW / 2, platformY);
    g.stroke();

    // sway: clapper from the sim; sail wider (and synthesised from the plate in poisson)
    const contact = vs.strikeModel === 'contact';
    const swayUnit = sw * 0.32;
    const clapSway = clamp(vs.clapper.x, -1.25, 1.25) * swayUnit;
    const sailRaw = contact ? vs.sail.x : vs.plate.x * 1.6;
    const sailSway = Math.tanh(sailRaw * 0.6) * 1.4 * swayUnit;

    // tubes: longest (lowest pitch) on the left → shortest (highest) on the right
    const tubes = [...vs.ring].sort((p, q) => p.midi - q.midi);
    const n = tubes.length;
    const tubeTop = platformY + 5;
    const maxLen = (bot - tubeTop) * 0.6;
    const lo = Math.min(...tubes.map((t) => t.midi));
    const hi = Math.max(...tubes.map((t) => t.midi));
    const spanW = platW * 0.9;
    // (poisson) detect this frame's strike so the clapper can visually meet the tube
    let struck: { x: number; y: number } | null = null;
    for (let k = 0; k < n; k++) {
      const t = tubes[k];
      const fx = n > 1 ? k / (n - 1) - 0.5 : 0;
      const tx = cx + fx * spanW;
      const lenFrac = hi > lo ? (hi - t.midi) / (hi - lo) : 1; // 1 = lowest/longest
      const tlen = maxLen * (0.5 + 0.5 * lenFrac);
      const lean = clapSway * 0.1; // tubes lean gently with the swing
      const xt = tx + lean;
      const flash = t.flash ?? 0;
      if (!contact) {
        const prev = this.sideFlashPrev.get(t.midi) ?? 0;
        if (flash > prev + 1e-4) struck = { x: xt, y: tubeTop + tlen };
        this.sideFlashPrev.set(t.midi, flash);
      }
      // hanger from the platform to the tube top
      g.strokeStyle = 'rgba(150,170,185,.28)';
      g.lineWidth = 1;
      g.beginPath();
      g.moveTo(tx, platformY);
      g.lineTo(xt, tubeTop);
      g.stroke();
      // the tube — lights up on strike
      if (flash > 0.05) {
        g.strokeStyle = accent;
        g.shadowColor = accent;
        g.shadowBlur = 16 * flash;
      } else {
        g.strokeStyle = t.isRoot ? 'rgba(175,195,210,.6)' : 'rgba(140,160,175,.42)';
        g.shadowBlur = 0;
      }
      g.lineWidth = t.isRoot ? 4 : 3;
      g.beginPath();
      g.moveTo(xt, tubeTop);
      g.lineTo(xt + lean * 0.4, tubeTop + tlen);
      g.stroke();
      g.shadowBlur = 0;
      if (this.showRing) {
        g.fillStyle = flash > 0.1 ? accent : 'rgba(150,170,185,.4)';
        g.font = '9px "Spline Sans Mono", ui-monospace, monospace';
        g.textAlign = 'center';
        g.fillText(midiName(t.midi), xt + lean * 0.4, tubeTop + tlen + 12);
      }
    }

    // clapper + sail hang at lengths set by their cords (L = g/ω²), so adjusting Clapper ω
    // visibly shortens/lengthens the cord here. A saturating map keeps the bob in frame
    // across the full ω range; in poisson mode (no live sail) fall back to the old fixed drop.
    const span = bot - platformY;
    const cordDrop = (L: number, base: number, gain: number, lo: number, hi: number): number =>
      span * clamp(base + gain * Math.tanh(L * 1.3), lo, hi);
    // The clapper hangs at a fixed optimal strike height (its cord length doesn't change — a real
    // chime keeps it aligned with the tube rods); the puck's SIZE is what the slider varies.
    const clapY = tubeTop + maxLen * 0.6;
    // Horizontal placement must share the tubes' axis. The side view is the ring "unrolled"
    // by index (tubes sorted by pitch = index order), so map the clapper's ring ANGLE onto
    // that same fractional-index axis — then the swung-out clapper lines up over the tube it's
    // actually striking (the lit one), instead of floating by its raw Cartesian x.
    let clapFk = ((Math.atan2(vs.clapper.y, vs.clapper.x) + Math.PI / 2) / (2 * Math.PI)) * n;
    clapFk = ((clapFk % n) + n) % n; // fractional ring index in [0, n)
    const clapFx = n > 1 ? clamp(clapFk / (n - 1) - 0.5, -0.55, 0.55) : 0;
    let clapX = contact ? cx + clapFx * spanW : cx + clapSway;
    let drawClapY = clapY;
    const capH = 11;
    if (!contact) {
      // strike animation: the capsule snaps to the struck tube at note time, holds a
      // beat, then eases back to its wind sway — so every tone shows a real contact
      if (struck) this.sideHit = { x: struck.x, y: Math.min(clapY, struck.y - capH / 2 - 2), t: 0 };
      else if (this.sideHit) this.sideHit.t += dt;
      if (this.sideHit) {
        const t = this.sideHit.t;
        const w = t < 0.09 ? 1 : Math.exp(-(t - 0.09) * 7); // hold ~90ms, then ease home
        if (w > 0.01) {
          clapX = clapX + (this.sideHit.x - clapX) * w;
          drawClapY = clapY + (this.sideHit.y - clapY) * w;
        } else {
          this.sideHit = null;
        }
      }
    }
    g.strokeStyle = 'rgba(150,170,185,.3)';
    g.lineWidth = 1;
    g.beginPath();
    g.moveTo(cx, platformY);
    g.lineTo(clapX, drawClapY);
    g.stroke();
    // the clapper: a horizontal capsule spanning the tubes it can reach (width tracks
    // the Size slider) — same design in both strike models
    const capW = clamp(vs.clapperRadius * platW * 1.5, 26, platW * 0.7);
    g.beginPath();
    g.roundRect(clapX - capW / 2, drawClapY - capH / 2, capW, capH, capH / 2);
    g.fillStyle = accent;
    g.shadowColor = accent;
    g.shadowBlur = 16;
    g.fill();
    g.shadowBlur = 0;

    // wind sail: hangs below the clapper on its own cord, swaying more (the wind-catcher)
    const sailY = contact
      ? Math.min(bot - 6, clapY + cordDrop(vs.cordLen.sail, 0.14, 0.42, 0.12, 0.62))
      : clapY + (bot - clapY) * 0.62;
    // the sail hangs from the clapper, so anchor it under clapX and let the sail's lateral
    // offset from the clapper (in sim space) sway it — keeps the cord from skewing across the view
    const sailX = contact
      ? clapX + clamp(vs.sail.x - vs.clapper.x, -1.5, 1.5) * swayUnit * 0.5
      : cx + sailSway;
    g.strokeStyle = 'rgba(150,170,185,.22)';
    g.lineWidth = 1;
    g.beginPath();
    g.moveTo(clapX, drawClapY);
    g.lineTo(sailX, sailY);
    g.stroke();
    // the sail plate: a vertical rounded rectangle in both strike models; its apparent
    // width pulses broadside↔edge-on with the spin (constant in poisson, where it's 0)
    const broad = 0.55 + 0.45 * Math.abs(Math.cos(vs.sailAngle));
    const pw = 27 * slim * broad;
    const ph = 44 * slim;
    g.beginPath();
    g.roundRect(sailX - pw / 2, sailY - ph / 2, pw, ph, 6 * slim);
    g.fillStyle = a(0.32);
    g.fill();
    g.strokeStyle = a(0.5);
    g.lineWidth = 1.5;
    g.stroke();

    g.restore();
  }
}
